import { Box, Stack } from '@mantine/core';
import { SettingsMultiSelect } from '@repo/common/components';
import type { IIntegration } from '@repo/common/models/integration';
import { Title } from '@repo/foundations';
import { upperFirst } from 'lodash-es';
import { memo, useCallback, useMemo } from 'react';
import {
	useSuspenseFeatureAccess,
	useUpdateIntegration,
	useUserGroupList,
} from '../../../../../api';

interface PreviewPermissionsPreferencesProps {
	integration: IIntegration;
}

export const PreviewPermissionsPreferences = memo(
	({ integration }: PreviewPermissionsPreferencesProps) => {
		const { mutateAsync: updateIntegration } = useUpdateIntegration({});
		const { groupAccess } = useSuspenseFeatureAccess();

		const { data: userGroups } = useUserGroupList({});
		const hasUserGroups = userGroups && userGroups.results.length > 0;

		const handleRoleChange = useCallback(
			(values: string[]) => {
				updateIntegration({
					data: {
						id: integration.id,
						preview_access_roles: values,
					},
				});
			},
			[integration.id, updateIntegration]
		);

		const handleGroupChange = useCallback(
			(values: string[]) => {
				updateIntegration({
					data: {
						id: integration.id,
						preview_access_groups: values,
					},
				});
			},
			[integration.id, updateIntegration]
		);

		const roles = useMemo(
			() => [
				{
					label: 'Admins',
					value: 'admin',
				},
				{
					label: 'Editors',
					value: 'editor',
				},
				{
					label: 'Viewers',
					value: 'viewer',
				},
			],
			[]
		);

		const groups = useMemo(
			() =>
				hasUserGroups
					? userGroups.results.map((group) => ({
							label: group.name,
							value: group.id,
						}))
					: [],
			[hasUserGroups, userGroups]
		);

		return (
			<Stack spacing="md">
				<Title size="md">Preview and column profile permissions</Title>
				<Box>
					<SettingsMultiSelect
						searchable
						title="Roles"
						description={`Select which roles can see previews and profile columns for ${upperFirst(integration.type)}.`}
						options={roles}
						values={integration.preview_access_roles ?? []}
						onChange={(values) => {
							handleRoleChange(values);
						}}
						itemName="role"
					/>
					{groupAccess && (
						<SettingsMultiSelect
							searchable
							title="Groups"
							description={`Select which groups can see previews and profile columns for ${upperFirst(integration.type)}.`}
							options={groups}
							values={integration.preview_access_groups ?? []}
							onChange={(values) => {
								handleGroupChange(values);
							}}
							itemName="group"
						/>
					)}
				</Box>
			</Stack>
		);
	}
);
